import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { invalidateApplicationQueries } from "shared/utils/invalidate-application-queries";
import { VerificationReport } from "types/landlord/reports";
import { getErrorMessage } from "utils/error";
import { CombinedIncomeReport } from "../../../types";

interface Props {
  activeVerificationId?: number;
  applicationId?: number;
  applicantId?: number;
  refetch: () => void;
  setShowCancelRerunModal: (value: boolean) => void;
  setCurrentlySelectedVerificationId: (value: number) => void;
}

type RerunResponse = {
  verification: VerificationReport<CombinedIncomeReport>;
};

export const useRerunVerificationController = ({
  activeVerificationId,
  applicationId,
  applicantId,
  refetch,
  setShowCancelRerunModal,
  setCurrentlySelectedVerificationId
}: Props) => {
  const queryClient = useQueryClient();

  // Both rerun endpoints share the same post-success flow: invalidate the
  // applicant + application caches, re-run the report query, and switch the
  // selected verification tab to whatever the BE hands back.
  const commonOnSuccess = async ({ verification }: RerunResponse) => {
    await queryClient.invalidateQueries({
      queryKey: [
        "APPLICATION",
        String(applicationId || 0),
        "applicant",
        String(applicantId || 0)
      ]
    });
    invalidateApplicationQueries(queryClient, applicationId || 0);
    refetch();
    setCurrentlySelectedVerificationId(verification.id);
  };

  const { mutate: rerun, isPending: isRerunning } = useMutation<RerunResponse>({
    mutationKey: [
      "RERUN_COMBINED_INCOME",
      "APPLICATION",
      String(applicationId || 0),
      "VERIFICATION",
      String(activeVerificationId || 0),
      "APPLICANT",
      String(applicantId || 0)
    ],
    mutationFn: () =>
      instance
        .post(
          `portal/v1/applications/${applicationId || 0}/applicants/${applicantId || 0}/verifications/${activeVerificationId || 0}/rerun`
        )
        .then(({ data }) => data),
    onError: (err) => {
      toast.error(getErrorMessage(err, "Error requesting income re-run"));
    },
    onSuccess: commonOnSuccess
  });

  const { mutate: cancelRerun, isPending: isCancelingRerun } =
    useMutation<RerunResponse>({
      mutationKey: [
        "CANCEL_COMBINED_INCOME_RERUN",
        "APPLICATION",
        String(applicationId || 0),
        "VERIFICATION",
        String(activeVerificationId || 0),
        "APPLICANT",
        String(applicantId || 0)
      ],
      mutationFn: () =>
        instance
          .post(
            `portal/v1/applications/${applicationId || 0}/applicants/${applicantId || 0}/verifications/${activeVerificationId || 0}/cancel_rerun`
          )
          .then(({ data }) => data),
      onError: (err) => {
        toast.error(getErrorMessage(err, "Failed to cancel income re-run"));
      },
      onSuccess: async (data) => {
        toast.success("Income re-run canceled");
        await commonOnSuccess(data);
        setShowCancelRerunModal(false);
      }
    });

  return {
    rerun,
    isRerunning,
    cancelRerun,
    isCancelingRerun
  };
};
